/**
 * avoa · comportamiento mínimo de las secciones avoa-*
 * - Header: clase `avoa-scrolled` al bajar (oculta la barra de anuncio y pega el header).
 * - Hero: el texto se desvanece al hacer scroll.
 * - Barra de compra móvil: clase `avoa-past-hero` al pasar el hero (o el bloque de compra).
 * - Selector de plan: precio, plan de suscripción y texto del botón.
 *
 * En escritorio (≥990px) Horizon hace scroll dentro de `.page-wrapper`, no en la ventana;
 * por eso se usa su helper `@theme/scroll-container`.
 */
import { getScrollTop } from '@theme/scroll-container';

const body = document.body;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let hero = null;
let heroCopy = null;
let buyAnchor = null;
let ticking = false;

function collectRefs() {
  hero = document.querySelector('[data-avoa-hero]');
  heroCopy = hero ? hero.querySelector('[data-avoa-hero-copy]') : null;
  buyAnchor = document.querySelector('[data-avoa-sticky-trigger]');
}

function update() {
  ticking = false;
  const y = getScrollTop();
  const vh = window.innerHeight;

  body.classList.toggle('avoa-scrolled', y > 40);

  let past = false;
  if (hero) {
    past = y > hero.offsetHeight * 0.7;
    if (!reduceMotion.matches) {
      if (heroCopy) heroCopy.style.opacity = String(Math.max(0, 1 - y / (vh * 0.55)));
    }
  } else if (buyAnchor) {
    past = buyAnchor.getBoundingClientRect().bottom < 0;
  }
  body.classList.toggle('avoa-past-hero', past);
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  window.requestAnimationFrame(update);
}

/* ---------- Selector de plan ---------- */

function initBuyBoxes(scope = document) {
  scope.querySelectorAll('[data-avoa-buy]').forEach((box) => {
    if (box.dataset.avoaReady) return;
    const dataEl = box.querySelector('[data-avoa-buy-data]');
    const form = box.querySelector('form');
    if (!dataEl || !form) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    box.dataset.avoaReady = 'true';

    const idInput = form.querySelector('input[name="id"]');
    const planInput = form.querySelector('input[name="selling_plan"]');
    const variantSelect = box.querySelector('[data-avoa-variant]');
    const planSelect = box.querySelector('[data-avoa-plan-select]');
    const submit = box.querySelector('[data-avoa-submit]');
    const submitLabel = submit ? submit.querySelector('[data-avoa-pill-label]') : null;

    const setText = (selector, text) => {
      box.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    };

    const render = () => {
      const checked = box.querySelector('[data-avoa-mode]:checked');
      const mode = checked ? checked.value : 'one';
      const variantId = variantSelect ? variantSelect.value : idInput.value;
      const variant = data.variants.find((v) => String(v.id) === String(variantId)) || data.variants[0];
      if (!variant) return;

      const planId = planSelect ? planSelect.value : data.defaultPlan;
      const allocation = planId ? variant.plans[String(planId)] : null;
      const isSub = mode === 'sub' && Boolean(allocation);

      idInput.value = variant.id;
      if (planInput) {
        planInput.value = isSub ? planId : '';
        planInput.disabled = !isSub;
      }

      setText('[data-avoa-price-one]', variant.price);
      setText('[data-avoa-compare]', variant.price);
      if (allocation) {
        setText('[data-avoa-price-sub]', allocation.price);
        setText('[data-avoa-save-text]', data.labels.save.replace('[ahorro]', allocation.save));
        box.querySelectorAll('[data-avoa-save]').forEach((el) => {
          el.hidden = !(allocation.save > 0);
        });
        box.querySelectorAll('[data-avoa-compare]').forEach((el) => {
          el.hidden = !(allocation.save > 0);
        });
      }

      if (submit && submitLabel) {
        if (!variant.available) {
          submit.disabled = true;
          submitLabel.textContent = data.labels.soldOut;
        } else {
          submit.disabled = false;
          const template = isSub ? data.labels.sub : data.labels.one;
          submitLabel.textContent = template.replace('[precio]', isSub ? allocation.price : variant.price);
        }
      }
    };

    box.addEventListener('change', render);
    render();
  });
}

function init() {
  collectRefs();
  initBuyBoxes();
  update();
}

document.addEventListener('scroll', onScroll, { passive: true, capture: true });
window.addEventListener('resize', onScroll, { passive: true });

// Editor de temas: volver a enlazar al recargar una sección
document.addEventListener('shopify:section:load', (event) => {
  collectRefs();
  initBuyBoxes(event.target);
  update();
});
document.addEventListener('shopify:section:unload', () => {
  window.requestAnimationFrame(() => {
    collectRefs();
    update();
  });
});

init();

/* ---------- Vídeos: se reproducen solo cuando están a la vista ---------- */
function initAutoplayVideos(scope = document) {
  const videos = scope.querySelectorAll('video[data-avoa-autoplay]');
  if (!videos.length || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) {
          v.preload = 'auto';
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    },
    { threshold: 0.5 }
  );
  videos.forEach((v) => {
    v.muted = true;
    io.observe(v);
  });
}
initAutoplayVideos();
document.addEventListener('shopify:section:load', (e) => initAutoplayVideos(e.target));

/* ---------- Antes y después ---------- */
function initBeforeAfter(scope = document) {
  scope.querySelectorAll('[data-avoa-ba]').forEach((el) => {
    const range = el.querySelector('input[type="range"]');
    if (!range || range.dataset.ready) return;
    range.dataset.ready = 'true';
    range.addEventListener('input', () => el.style.setProperty('--pos', `${range.value}%`));
  });
}
initBeforeAfter();
document.addEventListener('shopify:section:load', (e) => initBeforeAfter(e.target));

/* ---------- Selector de packs ---------- */
function initBundles(scope = document) {
  scope.querySelectorAll('[data-avoa-bundle]').forEach((box) => {
    if (box.dataset.avoaReady) return;
    const dataEl = box.querySelector('[data-avoa-bundle-data]');
    const form = box.querySelector('form');
    if (!dataEl || !form) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    box.dataset.avoaReady = 'true';

    const planInput = form.querySelector('input[name="selling_plan"]');
    const subToggle = box.querySelector('[data-avoa-sub]');
    const subLabel = box.querySelector('[data-avoa-sub-label]');
    const submit = box.querySelector('[data-avoa-submit]');
    const submitLabel = submit ? submit.querySelector('[data-avoa-pill-label]') : null;

    const render = () => {
      const checked = box.querySelector('[data-avoa-tier]:checked');
      if (!checked) return;
      const variant = data.variants[checked.value];
      if (!variant) return;

      const allocation = data.defaultPlan ? variant.plans[data.defaultPlan] : null;
      const isSub = Boolean(subToggle && subToggle.checked && allocation);
      if (planInput) {
        planInput.value = isSub ? data.defaultPlan : '';
        planInput.disabled = !isSub;
      }
      if (subLabel && data.labels.sub) {
        const save = allocation ? allocation.save : '';
        subLabel.textContent = data.labels.sub.replace('[ahorro]', save);
      }
      if (submit && submitLabel) {
        if (!variant.available) {
          submit.disabled = true;
          submitLabel.textContent = data.labels.soldOut;
        } else {
          submit.disabled = false;
          const price = isSub ? allocation.price : variant.price;
          submitLabel.textContent = data.labels.button.replace('[precio]', price);
        }
      }
    };

    box.addEventListener('change', render);
    render();
  });
}
initBundles();
document.addEventListener('shopify:section:load', (e) => initBundles(e.target));

/* ---------- Temporizador de oferta (fecha de fin real, no se reinicia) ---------- */
function initCountdowns(scope = document) {
  scope.querySelectorAll('[data-avoa-deadline]').forEach((el) => {
    if (el.dataset.ready) return;
    el.dataset.ready = 'true';
    const end = Date.parse(el.dataset.avoaDeadline.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
    const out = el.querySelector('[data-avoa-countdown]');
    if (!end || !out) return;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      const left = Math.floor((end - Date.now()) / 1000);
      if (left <= 0) {
        el.hidden = true;
        return;
      }
      const d = Math.floor(left / 86400);
      const h = Math.floor((left % 86400) / 3600);
      const m = Math.floor((left % 3600) / 60);
      const s = left % 60;
      out.textContent = `${d > 0 ? `${d} d ` : ''}${pad(h)}:${pad(m)}:${pad(s)}`;
      setTimeout(tick, 1000);
    };
    tick();
  });
}
initCountdowns();
document.addEventListener('shopify:section:load', (e) => initCountdowns(e.target));

/* ---------- Menú móvil: cerrar al tocar fuera o al elegir un enlace ---------- */
document.addEventListener('click', (event) => {
  const menu = document.querySelector('.avoa-header__mobile-menu[open]');
  if (!menu) return;
  const drawer = menu.querySelector('.avoa-header__drawer');
  const summary = menu.querySelector('summary');
  if (summary.contains(event.target)) return;
  if (!drawer.contains(event.target) || event.target.closest('a')) menu.removeAttribute('open');
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') document.querySelector('.avoa-header__mobile-menu[open]')?.removeAttribute('open');
});

/* ---------- Galería de producto: miniaturas ---------- */
function initGalleries(scope = document) {
  scope.querySelectorAll('[data-avoa-gallery]').forEach((gallery) => {
    if (gallery.dataset.ready) return;
    gallery.dataset.ready = 'true';
    const slides = gallery.querySelector('[data-avoa-slides]');
    const thumbs = [...gallery.querySelectorAll('[data-avoa-thumb]')];
    if (!slides || !thumbs.length) return;

    const setActive = (index) => {
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      const active = thumbs[index];
      if (active) {
        const bar = active.parentElement;
        const left = active.offsetLeft - (bar.clientWidth - active.clientWidth) / 2;
        bar.scrollTo({ left, behavior: 'smooth' });
      }
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const slide = slides.children[i];
        if (!slide) return;
        slides.scrollTo({ left: slide.offsetLeft - slides.offsetLeft - (parseFloat(getComputedStyle(slides).paddingLeft) || 0), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        setActive(i);
      });
    });

    let raf = 0;
    slides.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(slides.scrollLeft / (slides.children[0]?.offsetWidth || 1));
          setActive(Math.min(thumbs.length - 1, Math.max(0, i)));
        });
      },
      { passive: true }
    );
  });
}
initGalleries();
document.addEventListener('shopify:section:load', (e) => initGalleries(e.target));

/* ---------- Mini carrusel de reseñas (automático) ---------- */
function initQuotes(scope = document) {
  scope.querySelectorAll('[data-avoa-quotes]').forEach((box) => {
    if (box.dataset.ready) return;
    box.dataset.ready = 'true';
    const track = box.querySelector('[data-avoa-quotes-track]');
    const dots = [...box.querySelectorAll('.avoa-product__quotes-dots span')];
    const count = track ? track.children.length : 0;
    if (count < 2) return;
    let index = 0;
    let paused = false;
    const go = (i) => {
      index = (i + count) % count;
      track.style.transform = `translateX(${-100 * index}%)`;
      dots.forEach((d, j) => d.classList.toggle('is-active', j === index));
    };
    box.addEventListener('mouseenter', () => (paused = true));
    box.addEventListener('mouseleave', () => (paused = false));
    // Deslizar con el dedo
    let startX = null;
    box.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
    box.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
      startX = null;
    });
    const ms = (parseInt(box.dataset.speed, 10) || 5) * 1000;
    setInterval(() => {
      if (!paused && !document.hidden) go(index + 1);
    }, ms);
  });
}
initQuotes();
document.addEventListener('shopify:section:load', (e) => initQuotes(e.target));

/* ---------- Desplegables: al abrir uno se cierran los demás del mismo grupo ---------- */
document.addEventListener(
  'toggle',
  (event) => {
    const el = event.target;
    if (!(el instanceof HTMLDetailsElement) || !el.open || !el.matches('[data-avoa-acc]')) return;
    const group = el.closest('[data-avoa-acc-group]') || el.parentElement;
    group.querySelectorAll('details[data-avoa-acc][open]').forEach((other) => {
      if (other !== el) other.open = false;
    });
  },
  true
);

/* ---------- Carruseles con flechas y puntos (reseñas) ---------- */
function initSliders(scope = document) {
  scope.querySelectorAll('[data-avoa-slider]').forEach((root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const track = root.querySelector('[data-avoa-slider-track]');
    const prev = root.querySelector('[data-avoa-slider-prev]');
    const next = root.querySelector('[data-avoa-slider-next]');
    const dotsWrap = root.querySelector('[data-avoa-slider-dots]');
    if (!track) return;
    const items = [...track.children];
    const step = () => (items[1] ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth);
    const pages = () => Math.max(1, Math.round((track.scrollWidth - track.clientWidth) / step()) + 1);

    const renderDots = () => {
      if (!dotsWrap) return;
      const n = pages();
      if (dotsWrap.children.length !== n) {
        dotsWrap.innerHTML = '';
        for (let i = 0; i < n; i++) {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', `Ir a ${i + 1}`);
          b.addEventListener('click', () => track.scrollTo({ left: i * step(), behavior: 'smooth' }));
          dotsWrap.appendChild(b);
        }
      }
      const current = Math.round(track.scrollLeft / step());
      [...dotsWrap.children].forEach((d, i) => d.classList.toggle('is-active', i === current));
      dotsWrap.hidden = n < 2;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      renderDots();
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  });
}
initSliders();
document.addEventListener('shopify:section:load', (e) => initSliders(e.target));

/* ---------- Aparición suave de las secciones al hacer scroll ---------- */
function initReveal(scope = document) {
  if (reduceMotion.matches || !('IntersectionObserver' in window) || window.Shopify?.designMode) return;
  const targets = scope.querySelectorAll(
    '.avoa-section:not(.avoa-hero-section) .avoa-container > *, .avoa-section:not(.avoa-hero-section) [data-avoa-reveal]'
  );
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  const vh = window.innerHeight;
  targets.forEach((el) => {
    if (el.closest('.avoa-product-section') || el.dataset.revealed) return;
    // Lo que ya se ve al cargar no se anima
    if (el.getBoundingClientRect().top < vh) return;
    el.dataset.revealed = 'true';
    el.classList.add('avoa-reveal');
    io.observe(el);
  });
}
initReveal();
document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

/* ---------- Barra fija: "Añadir al carrito" envía el pack elegido ---------- */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-avoa-sticky-add]');
  if (!btn) return;
  const form = document.querySelector('[data-avoa-bundle] form, [data-avoa-buy] form');
  if (!form) return;
  event.preventDefault();
  if (form.requestSubmit) form.requestSubmit();
  else form.submit();
});

/* ---------- Carrusel de vídeos: reproducir al abrir la página ---------- */
function playLoopVideos(scope = document) {
  scope.querySelectorAll('.avoa-videos video').forEach((v) => {
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    const go = () => v.play().catch(() => {});
    if (v.readyState >= 2) go();
    else v.addEventListener('canplay', go, { once: true });
  });
}
playLoopVideos();
document.addEventListener('shopify:section:load', (e) => playLoopVideos(e.target));
// Si el navegador bloquea la reproducción automática, arranca con el primer toque
document.addEventListener('pointerdown', () => playLoopVideos(), { once: true, passive: true });

/* ---------- Enlaces a #avoa-buy: bajar suave hasta los packs ---------- */
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href="#avoa-buy"]');
  if (!link) return;
  const target = document.getElementById('avoa-buy');
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
});
